import React, {
  useCallback, useEffect, useMemo, useState, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import useInputState from '../../helpers/hooks/useInputState';
import { getOrganizationsRequest, cancelOrganizationsRequest } from '../../store/actions/organizations';
import OrganizationItem from './components/OrganizationItem';
import OrganizationModal from './components/OrganizationModal';
import {
  ORGANIZATION_FILTERS,
  ORGANIZATION_HEADER_COLUMNS,
  ORGANIZATION_MODAL_STATES,
  ORGANIZATION_WIDTHS,
} from './constants';
import MyButton from '../../components/MyButton';
import AddOrgIcon from '../../assets/formIcons/addOrg.svg';
import MyTable from '../../components/MyTable';
import BulkEditSelectedModal from '../../components/BulkEditSelectedModal';
import FiltersPanel, {
  FilterDateRange,
  FilterMultiSelect,
  FilterSelect,
  FiltersGroup,
} from '../../components/SearchFilters';
import useCitiesOfCountry from '../../helpers/hooks/useCitiesOfCountry';
import {
  getCategoriesRequest,
  getCountriesRequest,
  getLanguagesRequest,
} from '../../store/actions/staticData';
import {
  ONLINE_MODE_OPTIONS,
  ORGANIZATION_SORT_OPTIONS,
  ORGANIZATION_SOURCE_OPTIONS,
  buildOrganizationSearchParams,
  describeOrganizationFilters,
  formatSearchInfo,
  getDefaultOrganizationFilters,
  hasDateFiltersError,
  validateDateFilters,
} from '../../helpers/searchFilters';
import './css/index.scss';

function Organizations() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    organizations,
    organizationsTotal,
    organizationsAllTotal,
    organizationsActiveTotal,
    organizationsBlockedTotal,
    organizationsSearchInfo,
    organizationsLoading,
    organizationsErrorMessage,
  } = useSelector((store) => store.organizations);
  const { countries, categories, languages } = useSelector((store) => store.staticData);

  const [page, setPage] = useInputState(1);
  const [perPage, setPerPage] = useState(100);
  const [selectedFilter, setSelectedFilter] = useState(ORGANIZATION_FILTERS[0]);
  const [q, setQ] = useInputState();
  const [isInit, setIsInit] = useState(false);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalState, setModalState] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState({});
  const [itemsPassed, setItemsPassed] = useState(0);
  const abortControllerRef = useRef(null);
  const [selectedOrganizations, setSelectedOrganizations] = useState([]);
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false);
  const [filters, setFilters] = useState(getDefaultOrganizationFilters);
  const { cityOptions, citiesLoading } = useCitiesOfCountry(filters.countryId);

  const getOrganizations = ({ paramsPage = 1, paramsPerPage = perPage, isInitRequired = true } = {}) => {
    if (isInitRequired && !isInit) return;
    if (hasDateFiltersError(filters)) return;

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new AbortController
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    dispatch(getOrganizationsRequest(buildOrganizationSearchParams({
      q,
      page: paramsPage,
      perPage: paramsPerPage,
      isActive: selectedFilter.id,
      filters,
    }), abortController));

    setSelectedOrganizations([]);
  };

  const handleSearch = () => {
    setPage(1);
    getOrganizations({ paramsPage: 1 });
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    dispatch(cancelOrganizationsRequest());
  };

  const updateFilters = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  const resetFilters = () => setFilters((prev) => ({ ...getDefaultOrganizationFilters(), sort: prev.sort }));

  const countryOptions = useMemo(() => (countries || [])
    .filter((country) => !country.is_online)
    .map((country) => ({ value: country._id, label: country.name })), [countries]);

  const languageOptions = useMemo(
    () => (languages || []).map((language) => ({ value: language._id, label: language.name })),
    [languages],
  );

  const categoryOptions = useMemo(() => (categories || []).map((category) => ({
    value: category._id,
    label: `${category.name}${category.is_online ? ' — онлайн' : ''}`,
  })), [categories]);

  const subCategoryOptions = useMemo(() => {
    const category = (categories || []).find((item) => item._id === filters.categoryId);
    return (category?.sub_categories || []).map((item) => ({ value: item._id, label: item.name }));
  }, [categories, filters.categoryId]);

  const dateErrors = validateDateFilters(filters);
  const isOnlineOnly = filters.onlineMode === 'online';

  const filterChips = useMemo(() => describeOrganizationFilters(filters, {
    countries: countryOptions,
    cities: cityOptions,
    categories: categoryOptions,
    subCategories: subCategoryOptions,
    languages: languageOptions,
  }), [filters, countryOptions, cityOptions, categoryOptions, subCategoryOptions, languageOptions]);

  const extraFilters = (
    <FiltersPanel
      chips={filterChips}
      onRemoveChip={(chip) => updateFilters(chip.reset)}
      onReset={resetFilters}
    >
      <FiltersGroup title="Где">
        <FilterSelect
          label="Формат"
          value={filters.onlineMode}
          options={ONLINE_MODE_OPTIONS}
          allowEmpty={false}
          onChange={(onlineMode) => updateFilters({ onlineMode })}
          hint={filters.onlineMode === 'all' ? 'При выборе города онлайн-предложения тоже показываются' : undefined}
        />
        <FilterSelect
          label="Страна"
          value={filters.countryId}
          options={countryOptions}
          placeholder="Все страны"
          disabled={isOnlineOnly}
          onChange={(countryId) => updateFilters({ countryId, cityIds: [] })}
          hint={isOnlineOnly ? 'Онлайн-предложения доступны из любой страны' : undefined}
        />
        <FilterMultiSelect
          label="Города"
          options={cityOptions}
          values={filters.cityIds}
          onChange={(cityIds) => updateFilters({ cityIds })}
          placeholder="Все города"
          emptyText="В стране нет городов"
          loading={citiesLoading}
          disabled={isOnlineOnly || !filters.countryId}
          hint={!isOnlineOnly && !filters.countryId ? 'Сначала выберите страну' : undefined}
        />
      </FiltersGroup>

      <FiltersGroup title="Что">
        <FilterSelect
          label="Категория"
          value={filters.categoryId}
          options={categoryOptions}
          placeholder="Все категории"
          onChange={(categoryId) => updateFilters({ categoryId, subCategoryIds: [] })}
        />
        <FilterMultiSelect
          label="Подкатегории"
          options={subCategoryOptions}
          values={filters.subCategoryIds}
          onChange={(subCategoryIds) => updateFilters({ subCategoryIds })}
          placeholder="Все подкатегории"
          emptyText="У категории нет подкатегорий"
          disabled={!filters.categoryId || !subCategoryOptions.length}
          hint={!filters.categoryId ? 'Сначала выберите категорию' : (!subCategoryOptions.length ? 'У категории нет подкатегорий' : undefined)}
        />
        <FilterMultiSelect
          label="Языки"
          options={languageOptions}
          values={filters.languageIds}
          onChange={(languageIds) => updateFilters({ languageIds })}
          placeholder="Все языки"
          emptyText="Языки не загружены"
          hint="Плюс карточки «для всех языков»"
        />
      </FiltersGroup>

      <FiltersGroup title="Служебные">
        <FilterSelect
          label="Кто создал"
          value={filters.source}
          options={ORGANIZATION_SOURCE_OPTIONS}
          placeholder="Все"
          onChange={(source) => updateFilters({ source })}
        />
        <FilterDateRange
          label="Дата создания"
          from={filters.createdFrom}
          to={filters.createdTo}
          error={dateErrors.created}
          onChange={({ from, to }) => updateFilters({ createdFrom: from, createdTo: to })}
        />
        <FilterSelect
          label="Сортировка"
          value={filters.sort}
          options={ORGANIZATION_SORT_OPTIONS}
          allowEmpty={false}
          onChange={(sort) => updateFilters({ sort })}
          hint="Оплаченные (буст) всегда выше"
        />
      </FiltersGroup>
    </FiltersPanel>
  );

  const TableItemComponent = useCallback((item, index) => (
    <OrganizationItem
      key={item._id}
      item={item}
      itemsPassed={itemsPassed}
      widths={ORGANIZATION_WIDTHS}
      index={index}
      setSelectedOrganization={setSelectedOrganization}
      setModalState={setModalState}
      setIsModalVisible={setIsModalVisible}
      withCheckbox={selectedOrganizations.length > 0}
      isSelected={selectedOrganizations.includes(item._id)}
      setSelected={setSelectedOrganizations}
    />
  ), [itemsPassed, selectedOrganizations]);

  const WhileSelectingComponents = useCallback(() => (
    selectedOrganizations?.length ? (
      <div className="table_selection_bar">
        <span className="table_selection_bar_count">
          Выбрано:
          {' '}
          {selectedOrganizations.length}
        </span>
        <div
          className="pressable table_selection_bar_action"
          onClick={() => setSelectedOrganizations(organizations.map((item) => item._id))}
        >
          Выбрать все на странице
        </div>
        <div
          className="pressable table_selection_bar_action"
          onClick={() => setSelectedOrganizations([])}
        >
          Снять выбор со всех
        </div>
        <div
          onClick={() => setIsBulkEditOpen(true)}
          className="pressable table_selection_bar_action"
        >
          Редактировать
        </div>
        <div
          onClick={() => {
            setModalState(ORGANIZATION_MODAL_STATES.block);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Забанить
        </div>
        <div
          onClick={() => {
            setModalState(ORGANIZATION_MODAL_STATES.changeOwner);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Изменить владельца
        </div>
        <div
          onClick={() => {
            setModalState(ORGANIZATION_MODAL_STATES.fullyDelete);
            setIsModalVisible(true);
          }}
          className="pressable table_selection_bar_action"
        >
          Удалить полностью
        </div>
      </div>
    ) : null
  ), [selectedOrganizations, organizations]);

  const handlePage = (newVal) => {
    if (newVal === page) return;
    setPage(newVal);
    getOrganizations({ paramsPage: newVal });
  };

  const onCreateOrganizationClick = () => {
    navigate('/organizations/create');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);

      if (perPage < 1) {
        setPerPage(1);
        getOrganizations({ paramsPage: 1, paramsPerPage: 1 });
      } else if (perPage > 500) {
        setPerPage(500);
        getOrganizations({ paramsPage: 1, paramsPerPage: 500 });
      } else {
        getOrganizations({ paramsPage: 1 });
      }
    }, 600);

    return () => clearTimeout(timer);
  }, [perPage]);

  useEffect(() => {
    setPage(1);
    getOrganizations({ paramsPage: 1 });
  }, [selectedFilter]);

  // Фильтры применяются сразу (с небольшой задержкой, чтобы не слать запрос на каждый клик).
  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    if (!isInit) return undefined;
    const timer = setTimeout(() => {
      setPage(1);
      getOrganizations({ paramsPage: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [filtersKey]);

  useEffect(() => {
    dispatch(getCountriesRequest());
    dispatch(getCategoriesRequest());
    dispatch(getLanguagesRequest());
  }, []);

  useEffect(() => {
    getOrganizations({ paramsPage: page, isInitRequired: false });
    setTimeout(() => {
      setIsInit(true);
    }, 500);
  }, []);

  useEffect(() => {
    setItemsPassed((page - 1) * perPage);
  }, [organizations]);

  return (
    <div>
      <PageTitle
        text="Предложения (во всех странах)"
        RightComponent={(
          <MyButton
            text="Добавить"
            svgPath={AddOrgIcon}
            className="org_add_btn"
            onClick={onCreateOrganizationClick}
          />
        )}
      />
      <MyTable
        q={q}
        setQ={setQ}
        filters={ORGANIZATION_FILTERS}
        filtersCount={{
          [ORGANIZATION_FILTERS[0].id]: organizationsAllTotal || 0,
          [ORGANIZATION_FILTERS[1].id]: organizationsActiveTotal || 0,
          [ORGANIZATION_FILTERS[2].id]: organizationsBlockedTotal || 0,
        }}
        perPage={perPage}
        setPerPage={setPerPage}
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
        // headerColumns={ORGANIZATION_HEADER_COLUMNS}
        Item={TableItemComponent}
        WhileSelectingComponents={WhileSelectingComponents}
        data={organizations}
        page={page}
        setPage={handlePage}
        total={organizationsTotal}
        onSearch={handleSearch}
        isLoading={organizationsLoading}
        onCancelSearch={handleCancelSearch}
        extraFilters={extraFilters}
        searchHint={formatSearchInfo(organizationsSearchInfo)}
        errorText={organizationsErrorMessage}
      />

      <OrganizationModal
        selectedOrganization={selectedOrganization}
        modalState={modalState}
        isVisible={isModalVisible}
        setIsVisible={setIsModalVisible}
        setSelectedOrganization={setSelectedOrganization}
        selectedOrganizations={selectedOrganizations}
        setSelectedOrganizations={setSelectedOrganizations}
        getOrganizations={getOrganizations}
      />

      <BulkEditSelectedModal
        isOpen={isBulkEditOpen}
        onClose={() => setIsBulkEditOpen(false)}
        entityType="organization"
        entityLabel="предложений"
        selectedItems={organizations.filter((item) => selectedOrganizations.includes(item._id))}
        onFinished={() => {
          setSelectedOrganizations([]);
          getOrganizations({ paramsPage: page });
        }}
      />
    </div>
  );
}

export default Organizations;
